import React, { useState } from "react";
import { MapPin, Briefcase, TrendingUp, Heart, GraduationCap, Coffee, ArrowRight, Mail, Upload, Phone, Users } from "lucide-react";
import CtaSection, { CtaButton } from "../components/common/CtaSection";
import LeadCaptureModal from "../components/common/LeadCaptureModal";
import { LEAD_CHANNELS, CONTACT_EMAIL } from "../constants/data";

const GOLD = "#50C878";
const NAVY = "#013220";

// ─── CAREERS ──────────────────────────────────────────────────
// Editable open roles + perks. "Apply" opens the lead modal
// prefilled with the role, and there's an email CTA for resumes.
// ─────────────────────────────────────────────────────────────

const OPENINGS = [
  { 
    title: "Tele Marketing Manager", 
    type: "Full-time", 
    location: "Banjara Hills, Hyderabad", 
    vacancies: 1,
    body: "Lead our telemarketing team to drive buyer engagement and conversions. Develop strategies, coach team members, and ensure high-quality lead nurturing." 
  },
  { 
    title: "Tele Marketing Executive", 
    type: "Full-time", 
    location: "Hyderabad", 
    vacancies: 4,
    body: "Connect with potential buyers, understand their requirements, and guide them through our platform. Build relationships and convert leads into successful sales." 
  },
];

const PERKS = [
  { icon: TrendingUp, title: "Real Growth", body: "A fast-growing platform means fast-growing careers. We promote from within." },
  { icon: Heart, title: "Buyer-First Culture", body: "We work for buyers, and we treat our team the same way — with respect and honesty." },
  { icon: GraduationCap, title: "Learn Constantly", body: "Hands-on exposure to real estate, negotiation, legal and marketing under one roof." },
  { icon: Coffee, title: "A Good Place to Work", body: "A central Banjara Hills office, a supportive team, and work that genuinely helps families." },
];

export default function CareersPage() {
  const [showModal, setShowModal] = useState(false);
  const [roleContext, setRoleContext] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");

  function apply(role) {
    setRoleContext(role);
    setShowModal(true);
  }

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      setUploadStatus(`Selected: ${file.name}`);
    }
  };

  const handleSubmitApplication = () => {
    if (!resumeFile) {
      setUploadStatus("Please upload your resume first");
      return;
    }
    
    // Here you would typically upload the file to your server
    // For now, we'll open mailto with attachment reference
    const subject = encodeURIComponent(`Career Application - ${roleContext || "General Application"}`);
    const body = encodeURIComponent(`Please find attached my resume.\n\nPosition: ${roleContext || "General Application"}\n\nBest regards,`);
    
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setUploadStatus("Opening email client...");
    
    // Reset after a moment
    setTimeout(() => {
      setUploadStatus("");
      setResumeFile(null);
    }, 3000);
  };

  return (
    <div style={{ background: "#F2F8F3", color: NAVY, minHeight: "100vh", paddingTop: "68px" }}>
      {/* Hero */}
      <div style={{ padding: "64px 24px 44px", textAlign: "center", background: "linear-gradient(135deg, rgba(80,200,120,0.10), rgba(127,217,160,0.04))", borderBottom: "1px solid rgba(80,200,120,0.2)" }}>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          Join the Team
        </span>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.4rem)", fontWeight: 600, margin: "12px 0 16px" }}>
          Careers at GroupBuy Flats
        </h1>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.05rem", color: "rgba(1,50,32,0.7)", lineHeight: 1.7, maxWidth: "680px", margin: "0 auto 28px" }}>
          We're building Hyderabad's most trusted real estate platform—and we're looking for passionate minds ready to disrupt the real estate buying process. If you want to help families buy better, let's connect.
        </p>
        <a href="#openings" style={{ 
          display: "inline-flex",
          alignItems: "center",
          gap: "10px",
          background: `linear-gradient(135deg, ${GOLD}, #7FD9A0)`,
          border: "none",
          borderRadius: "8px",
          padding: "14px 32px",
          color: "#FFFFFF",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          fontSize: "1rem",
          textDecoration: "none",
          cursor: "pointer",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}>
          See Open Roles
          <ArrowRight size={18} />
        </a>
      </div>

      {/* Perks */}
      <section style={{ maxWidth: "1100px", margin: "0 auto", padding: "60px 24px 30px" }}>
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 600, margin: 0 }}>Why Work With Us</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "22px" }}>
          {PERKS.map(({ icon: Icon, title, body }) => (
            <div key={title} style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)", border: "1px solid rgba(80,200,120,0.18)", borderRadius: "14px", padding: "26px" }}>
              <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(80,200,120,0.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                <Icon size={22} color={GOLD} />
              </div>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.35rem", fontWeight: 600, margin: "0 0 8px" }}>{title}</h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.92rem", color: "rgba(1,50,32,0.72)", lineHeight: 1.7, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Open Roles Section */}
      <section id="openings" style={{ maxWidth: "1100px", margin: "0 auto", padding: "40px 24px 60px" }}>
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 600, margin: 0 }}>Current Openings</h2>
          <p style={{ fontFamily: "'Inter', sans-serif", color: "rgba(1,50,32,0.6)", marginTop: "8px" }}>
            Join our team and make a difference in the real estate industry
          </p>
        </div>

        <div style={{ display: "grid", gap: "24px" }}>
          {OPENINGS.map(({ title, type, location, vacancies, body }) => (
            <div 
              key={title} 
              style={{ 
                background: "#FFFFFF", 
                border: "1px solid rgba(80,200,120,0.15)", 
                borderRadius: "16px", 
                padding: "32px",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.06)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
                <div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 600, margin: "0 0 6px" }}>
                    {title}
                  </h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "12px" }}>
                    <span style={{ 
                      display: "inline-flex", 
                      alignItems: "center", 
                      gap: "6px", 
                      fontSize: "0.85rem", 
                      color: NAVY,
                      background: "rgba(80,200,120,0.08)",
                      padding: "4px 12px",
                      borderRadius: "20px",
                    }}>
                      <Briefcase size={14} />
                      {type}
                    </span>
                    <span style={{ 
                      display: "inline-flex", 
                      alignItems: "center", 
                      gap: "6px", 
                      fontSize: "0.85rem", 
                      color: NAVY,
                      background: "rgba(1,50,32,0.06)",
                      padding: "4px 12px",
                      borderRadius: "20px",
                    }}>
                      <MapPin size={14} />
                      {location}
                    </span>
                    <span style={{ 
                      display: "inline-flex", 
                      alignItems: "center", 
                      gap: "6px", 
                      fontSize: "0.85rem", 
                      color: GOLD,
                      background: "rgba(80,200,120,0.10)",
                      padding: "4px 12px",
                      borderRadius: "20px",
                    }}>
                      <Users size={14} />
                      {vacancies} {vacancies > 1 ? 'Vacancies' : 'Vacancy'}
                    </span>
                  </div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", color: "rgba(1,50,32,0.75)", lineHeight: 1.6, margin: "0 0 16px" }}>
                    {body}
                  </p>
                </div>
                <button 
                  onClick={() => apply(title)}
                  style={{
                    background: "linear-gradient(135deg, #50C878, #7FD9A0)",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 28px",
                    color: "#FFFFFF",
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "scale(1.02)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(80,200,120,0.3)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Apply Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Application Section with Resume Upload */}
      <div style={{ maxWidth: "700px", margin: "0 auto", padding: "40px 24px 80px" }}>
        <div style={{ 
          background: "#FFFFFF", 
          border: "1px solid rgba(80,200,120,0.15)", 
          borderRadius: "16px", 
          padding: "48px 40px",
          textAlign: "center"
        }}>
          <div style={{ width: "60px", height: "3px", background: GOLD, margin: "0 auto 32px", borderRadius: "2px" }} />
          
          <h2 style={{ 
            fontFamily: "'Cormorant Garamond', serif", 
            fontSize: "clamp(1.8rem, 3vw, 2.4rem)", 
            fontWeight: 600, 
            margin: "0 0 12px",
            lineHeight: 1.2
          }}>
            Upload Your Resume
          </h2>
          
          <p style={{ 
            fontFamily: "'Inter', sans-serif", 
            fontSize: "1.05rem", 
            color: "rgba(1,50,32,0.7)", 
            lineHeight: 1.8, 
            margin: "0 0 8px" 
          }}>
            Send your application to
          </p>
          
          <p style={{ 
            fontFamily: "'Inter', sans-serif", 
            fontSize: "1.1rem", 
            color: GOLD, 
            fontWeight: 600,
            margin: "0 0 28px" 
          }}>
            {CONTACT_EMAIL}
          </p>
          
          <div style={{ 
            width: "80px", 
            height: "1px", 
            background: "rgba(80,200,120,0.3)", 
            margin: "28px auto" 
          }} />

          {/* Resume Upload Area */}
          <div style={{ marginBottom: "24px" }}>
            <label 
              htmlFor="resume-upload"
              style={{
                display: "block",
                width: "100%",
                padding: "40px 20px",
                border: "2px dashed rgba(80,200,120,0.3)",
                borderRadius: "12px",
                background: "rgba(247,244,236,0.5)",
                cursor: "pointer",
                transition: "border-color 0.2s, background 0.2s",
                textAlign: "center",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = GOLD;
                e.currentTarget.style.background = "rgba(247,244,236,0.8)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = "rgba(80,200,120,0.3)";
                e.currentTarget.style.background = "rgba(247,244,236,0.5)";
              }}
            >
              <Upload size={40} color={GOLD} style={{ marginBottom: "12px" }} />
              <p style={{ 
                fontFamily: "'Inter', sans-serif", 
                fontSize: "1rem", 
                color: NAVY,
                margin: 0,
                fontWeight: 500
              }}>
                Click to upload your resume
              </p>
              <p style={{ 
                fontFamily: "'Inter', sans-serif", 
                fontSize: "0.85rem", 
                color: "rgba(1,50,32,0.5)",
                margin: "8px 0 0"
              }}>
                PDF, DOC, DOCX (Max 5MB)
              </p>
            </label>
            <input
              id="resume-upload"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleResumeUpload}
              style={{ display: "none" }}
            />
            {resumeFile && (
              <div style={{ 
                marginTop: "12px", 
                fontFamily: "'Inter', sans-serif", 
                fontSize: "0.9rem", 
                color: NAVY,
                background: "rgba(80,200,120,0.08)",
                padding: "8px 16px",
                borderRadius: "8px",
                display: "inline-block"
              }}>
                ✅ {resumeFile.name}
              </div>
            )}
          </div>

          {uploadStatus && (
            <p style={{ 
              fontFamily: "'Inter', sans-serif", 
              fontSize: "0.9rem", 
              color: uploadStatus.includes("Please") ? "#D32F2F" : GOLD,
              margin: "0 0 16px"
            }}>
              {uploadStatus}
            </p>
          )}

          <button
            onClick={handleSubmitApplication}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "12px",
              background: "linear-gradient(135deg, #50C878, #7FD9A0)",
              border: "none",
              borderRadius: "8px",
              padding: "16px 36px",
              color: "#FFFFFF",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: "1.1rem",
              textDecoration: "none",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 28px rgba(80,200,120,0.35)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <Mail size={20} />
            Submit Application
          </button>
          
          <p style={{ 
            fontFamily: "'Inter', sans-serif", 
            fontSize: "0.8rem", 
            color: "rgba(1,50,32,0.35)", 
            marginTop: "24px",
            letterSpacing: "0.02em"
          }}>
            We'll review your application and get back to you within 48 hours.
          </p>
        </div>
      </div>

      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        channel={LEAD_CHANNELS.WEB}
        prefillMessage={roleContext ? `Career application: ${roleContext}` : "Career application"}
      />
    </div>
  );
}